import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { useAuth } from '@/lib/auth';
import { PageTitle } from '@/components/layout/PageTitle';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface AccountDetail {
    id: string;
    status: string;
    tenant_handle: string;
    environment: string;
    isolation_level: string;
    created: string;
    modified: string;
    organization_id: string;
    organization_name: string;
    organization_slug: string;
    org_code: string;
    application_id: string;
    application_key: string;
    application_name?: string;
    subdomain?: string;
}

interface AccountUser {
    id: string;
    user_id: string;
    role: string;
    status: string;
    created: string;
}

export default function AccountDetailPage() {
    const { id } = useParams<{ id: string }>();
    const { fetchWithAuth } = useAuth();
    const [account, setAccount] = useState<AccountDetail | null>(null);
    const [users, setUsers] = useState<AccountUser[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!id) return;
        Promise.all([
            fetchWithAuth(`/api/accounts/${id}`).then((r) => r.json()),
            fetchWithAuth(`/api/accounts/${id}/users`).then((r) => r.json()),
        ])
            .then(([acctData, usersData]) => {
                setAccount(acctData);
                setUsers(usersData.data ?? []);
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [id, fetchWithAuth]);

    if (loading) return <div className="text-muted-foreground text-sm p-6">Loading...</div>;
    if (!account) return <div className="text-muted-foreground text-sm p-6">Account not found</div>;

    return (
        <div className="space-y-6">
            <PageTitle
                title={account.tenant_handle}
                subtitle={`${account.environment} account on ${account.application_name ?? account.application_key}`}
            />

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Badge variant={account.status === 'Active' ? 'default' : 'secondary'}>
                            {account.status}
                        </Badge>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Environment</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Badge variant="outline">{account.environment}</Badge>
                        <div className="mt-1 text-xs text-muted-foreground">
                            Isolation: {account.isolation_level}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Organization</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="font-medium">{account.organization_name}</div>
                        <div className="text-xs text-muted-foreground">{account.org_code}</div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Application</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="font-medium">{account.application_name ?? account.application_key}</div>
                        {account.subdomain && (
                            <div className="text-xs text-muted-foreground">{account.subdomain}</div>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Members</CardTitle>
                </CardHeader>
                <CardContent>
                    {users.length === 0 ? (
                        <div className="text-muted-foreground text-sm">No members yet.</div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>User ID</TableHead>
                                    <TableHead>Role</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Joined</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.map((u) => (
                                    <TableRow key={u.id}>
                                        <TableCell className="font-mono text-sm">{u.user_id.slice(0, 8)}...</TableCell>
                                        <TableCell>
                                            <Badge variant={u.role === 'owner' ? 'default' : 'outline'}>{u.role}</Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={u.status === 'Active' ? 'default' : 'secondary'}>{u.status}</Badge>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-sm">
                                            {new Date(u.created).toLocaleDateString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
